import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Building2, Loader2, MapPin, Plus, Search, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { fetchCepData, fetchCnpjData, formatCep, formatCpfCnpj, formatTelefone, mergeFilled, onlyDigits } from "@/lib/brasil";

const empty = { nome: "", cpf_cnpj: "", email: "", telefone: "", endereco: "", cidade: "", estado: "", cep: "" };

export default function Clientes() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [lookup, setLookup] = useState({ cnpj: false, cep: false });

  const load = useCallback(async () => {
    const { data } = await api.get("/clientes", { params: q ? { q } : {} });
    setItems(data);
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updateCpfCnpj = (e) => setForm((f) => ({ ...f, cpf_cnpj: formatCpfCnpj(e.target.value) }));
  const updateCep = (e) => setForm((f) => ({ ...f, cep: formatCep(e.target.value) }));
  const updateTelefone = (e) => setForm((f) => ({ ...f, telefone: formatTelefone(e.target.value) }));

  const lookupCnpj = async () => {
    if (onlyDigits(form.cpf_cnpj).length !== 14) {
      toast.warning("Digite um CNPJ válido para consultar.");
      return;
    }
    setLookup((s) => ({ ...s, cnpj: true }));
    try {
      const data = await fetchCnpjData(form.cpf_cnpj);
      setForm((f) => ({ ...f, ...mergeFilled(f, data) }));
      toast.success("Dados do CNPJ preenchidos");
    } catch (err) {
      toast.error(err.message || "Não foi possível consultar o CNPJ");
    } finally {
      setLookup((s) => ({ ...s, cnpj: false }));
    }
  };

  const lookupCep = async ({ silent = false } = {}) => {
    if (onlyDigits(form.cep).length !== 8 || lookup.cep) return;
    setLookup((s) => ({ ...s, cep: true }));
    try {
      const data = await fetchCepData(form.cep);
      setForm((f) => ({ ...f, ...mergeFilled(f, data) }));
      if (!silent) toast.success("Endereço preenchido pelo CEP");
    } catch (err) {
      if (!silent) toast.error(err.message || "Não foi possível consultar o CEP");
    } finally {
      setLookup((s) => ({ ...s, cep: false }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/clientes/${editId}`, form);
      else await api.post("/clientes", form);
      toast.success(editId ? "Cliente atualizado" : "Cliente cadastrado");
      setOpen(false); setForm(empty); setEditId(null);
      load();
    } catch (err) {
      toast.error("Erro ao salvar cliente");
    }
  };

  const edit = (c) => { setForm(c); setEditId(c.id); setOpen(true); };
  const del = (c) => {
  toast(`Excluir cliente ${c.nome}?`, {
    description: "Essa ação removerá o cliente da base de dados.",
    duration: Infinity,
    action: {
      label: "Excluir",
      onClick: async () => {
        try {
          await api.delete(`/clientes/${c.id}`);
          toast.success("Cliente excluído com sucesso");
          load();
        } catch {
          toast.error("Erro ao excluir o cliente");
        }
      },
    },
    cancel: { label: "Cancelar" },
  });
};

  return (
    <div className="space-y-6" data-testid="clientes-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Cadastros</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Clientes</h1>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditId(null); } }}>
          <DialogTrigger asChild>
            <Button className="rounded-sm" data-testid="clientes-new-button">
              <Plus className="w-4 h-4 mr-2" /> Novo cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl rounded-sm w-[95vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display">{editId ? "Editar cliente" : "Novo cliente"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Nome / Razão Social" required value={form.nome} onChange={update("nome")} testid="cliente-nome" className="sm:col-span-2" />
              <Field
                label="CPF / CNPJ"
                required
                value={form.cpf_cnpj}
                onChange={updateCpfCnpj}
                mono
                testid="cliente-cpf"
                action={
                  <Button type="button" variant="outline" size="sm" className="h-10 rounded-sm" onClick={lookupCnpj} disabled={lookup.cnpj || onlyDigits(form.cpf_cnpj).length !== 14} aria-label="Consultar CNPJ" title="Consultar CNPJ">
                    {lookup.cnpj ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Building2 className="w-3.5 h-3.5" />}
                  </Button>
                }
              />
              <Field label="E-mail" type="email" value={form.email} onChange={update("email")} testid="cliente-email" />
              <Field label="Telefone" value={form.telefone} onChange={updateTelefone} mono testid="cliente-telefone" />
              <Field
                label="CEP"
                value={form.cep}
                onChange={updateCep}
                onBlur={() => lookupCep({ silent: true })}
                mono
                testid="cliente-cep"
                action={
                  <Button type="button" variant="outline" size="sm" className="h-10 rounded-sm" onClick={() => lookupCep()} disabled={lookup.cep || onlyDigits(form.cep).length !== 8} aria-label="Consultar CEP" title="Consultar CEP">
                    {lookup.cep ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
                  </Button>
                }
              />
              <Field label="Endereço" value={form.endereco} onChange={update("endereco")} testid="cliente-endereco" className="sm:col-span-2" />
              <Field label="Cidade" value={form.cidade} onChange={update("cidade")} testid="cliente-cidade" />
              <Field label="UF" value={form.estado} onChange={update("estado")} mono testid="cliente-uf" />
              <DialogFooter className="sm:col-span-2 mt-2">
                <Button type="submit" className="rounded-sm" data-testid="cliente-submit">{editId ? "Salvar" : "Cadastrar"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-sm border border-zinc-200 shadow-none">
        <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
          <Search className="w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Buscar por nome, CPF/CNPJ ou e-mail…"
            className="rounded-sm border-0 shadow-none focus-visible:ring-0 px-0"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="clientes-search"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="tbl" data-testid="clientes-table">
            <thead>
              <tr>
                <th>Nome</th><th>CPF/CNPJ</th><th>E-mail</th><th>Cidade/UF</th><th className="w-24 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={5} className="text-center py-12 text-sm text-zinc-500">Nenhum cliente cadastrado.</td></tr>
              )}
              {items.map((c) => (
                <tr key={c.id} data-testid={`cliente-row-${c.id}`}>
                  <td className="font-medium">{c.nome}</td>
                  <td className="font-mono text-xs">{c.cpf_cnpj}</td>
                  <td className="text-zinc-600">{c.email || "-"}</td>
                  <td className="text-zinc-600">{c.cidade ? `${c.cidade}/${c.estado || ""}` : "-"}</td>
                  <td className="text-right">
                    <button onClick={() => edit(c)} className="p-1.5 hover:bg-zinc-100 rounded-sm" data-testid={`cliente-edit-${c.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => del(c)} className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded-sm" data-testid={`cliente-delete-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, mono, className = "", testid, action, ...props }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{label}</Label>
      <div className="flex gap-2">
        <Input {...props} className={`rounded-sm h-10 ${mono ? "font-mono text-xs" : ""}`} data-testid={testid} />
        {action}
      </div>
    </div>
  );
}
